// src/pages/AdminDashboardPage.tsx
import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  IconButton,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  Select,
  MenuItem,
  LinearProgress,
  CircularProgress,
  Snackbar,
  Alert,
  Checkbox,
  InputAdornment,
  Grid,
  Card,
  CardContent,
  Toolbar,
  AppBar,
  Divider,
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  PersonAdd as PersonAddIcon,
  Edit as EditIcon,
  Sync as SyncIcon,
  VpnKey as KeyIcon,
  DeleteOutline as DeleteIcon,
  AdminPanelSettings as AdminIcon,
  FolderOpen as FolderIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Folder as DriveIcon,
  Logout as LogoutIcon,
  People as PeopleIcon,
  CheckCircle as ActiveIcon,
  Storage as StorageIcon,
} from "@mui/icons-material";
import { getAuthHeaders, getCurrentUser, logout } from "../app/auth";
import { humanReadableSize } from "../app/utils";
import { calculatePhysicalTotalUsedBytes } from "../app/quotaEngine";

export interface UserItem {
  id?: string;
  username: string;
  role: "admin" | "user";
  enabled: boolean;
  rootPath: string;
  permission: "rw" | "ro";
  quotaBytes: number;
  usedBytes: number;
  createdAt?: number;
  updatedAt?: number;
}

interface AdminDashboardPageProps {
  onNavigateToDrive: () => void;
  onLogout: () => void;
}

export const AdminDashboardPage: React.FC<AdminDashboardPageProps> = ({
  onNavigateToDrive,
  onLogout,
}) => {
  const [users, setUsers] = useState<UserItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [toast, setToast] = useState<{ open: boolean; message: string; severity: "success" | "error" | "info" }>({
    open: false,
    message: "",
    severity: "info",
  });

  const [userActionLoading, setUserActionLoading] = useState<Record<string, string>>({});

  // 模态框控制
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<UserItem | null>(null);

  // 表单状态
  const [formUsername, setFormUsername] = useState("");
  const [formPassword, setFormPassword] = useState("");
  const [formRole, setFormRole] = useState<"admin" | "user">("user");
  const [formPermission, setFormPermission] = useState<"rw" | "ro">("rw");
  const [formRootPath, setFormRootPath] = useState("");
  const [formQuotaUnit, setFormQuotaUnit] = useState<"MB" | "GB" | "UNLIMITED">("UNLIMITED");
  const [formQuotaValue, setFormQuotaValue] = useState<number>(500);
  const [formShowPassword, setFormShowPassword] = useState(false);
  const [formSubmitting, setFormSubmitting] = useState(false);

  // 快捷重置密码模态框
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [targetPasswordUser, setTargetPasswordUser] = useState<string>("");
  const [newPasswordValue, setNewPasswordValue] = useState("");
  const [newPasswordShow, setNewPasswordShow] = useState(false);
  const [passwordSubmitting, setPasswordSubmitting] = useState(false);

  // 删除确认模态框
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [targetDeleteUser, setTargetDeleteUser] = useState<UserItem | null>(null);
  const [deleteFilesCascade, setDeleteFilesCascade] = useState(false);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  const currentUser = getCurrentUser();

  const showToast = (message: string, severity: "success" | "error" | "info" = "info") => {
    setToast({ open: true, message, severity });
  };

  /**
   * 获取用户列表
   */
  const fetchUsers = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);

    try {
      const res = await fetch("/api/center/users", {
        headers: getAuthHeaders({ forAdmin: true }),
      });

      if (!res.ok) {
        throw new Error(`获取用户列表失败: HTTP ${res.status}`);
      }

      const data: any = await res.json();
      const userList: UserItem[] = Array.isArray(data?.users) ? data.users : [];
      setUsers(userList);
    } catch (err: any) {
      console.error("fetchUsers error:", err);
      showToast(err?.message || "获取用户列表失败", "error");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // 统计数据
  const metrics = useMemo(() => {
    const totalUsers = users.length;
    const activeUsers = users.filter((u) => u.enabled).length;
    const totalUsedBytes = calculatePhysicalTotalUsedBytes(users);
    const adminCount = users.filter((u) => u.role === "admin").length;
    return {
      totalUsers,
      activeUsers,
      totalUsedBytes,
      adminCount,
    };
  }, [users]);

  /**
   * 一键校准用户已用容量
   */
  const handleRecalcQuota = async (username: string) => {
    setUserActionLoading((prev) => ({ ...prev, [username]: "recalc" }));
    try {
      const res = await fetch(`/api/center/users/${encodeURIComponent(username)}/recalc-quota`, {
        method: "POST",
        headers: getAuthHeaders({ forAdmin: true }),
      });

      if (!res.ok) {
        const errData: any = await res.json().catch(() => ({}));
        throw new Error(errData?.error || `校准失败: HTTP ${res.status}`);
      }

      const result: any = await res.json();
      showToast(`已成功校准用户 "${username}" 的物理占用空间: ${humanReadableSize(result.usedBytes)}`, "success");

      setUsers((prev) =>
        prev.map((u) => (u.username === username ? { ...u, usedBytes: result.usedBytes } : u))
      );
    } catch (err: any) {
      showToast(err?.message || "校准空间失败", "error");
    } finally {
      setUserActionLoading((prev) => {
        const next = { ...prev };
        delete next[username];
        return next;
      });
    }
  };

  /**
   * 一键校准所有用户空间
   */
  const [recalcingAll, setRecalcingAll] = useState(false);
  const handleRecalcAll = async () => {
    if (users.length === 0) return;
    setRecalcingAll(true);
    try {
      let successCount = 0;
      for (const u of users) {
        try {
          const res = await fetch(`/api/center/users/${encodeURIComponent(u.username)}/recalc-quota`, {
            method: "POST",
            headers: getAuthHeaders({ forAdmin: true }),
          });
          if (res.ok) {
            const data: any = await res.json();
            setUsers((prev) =>
              prev.map((item) => (item.username === u.username ? { ...item, usedBytes: data.usedBytes } : item))
            );
            successCount++;
          }
        } catch {
          // 忽略单个失败继续下一个
        }
      }
      showToast(`已完成 ${successCount}/${users.length} 个账号的存储空间校准`, "success");
    } catch (err: any) {
      showToast(err?.message || "批量校准失败", "error");
    } finally {
      setRecalcingAll(false);
    }
  };

  /**
   * 切换账号启用/停用状态
   */
  const handleToggleEnabled = async (user: UserItem) => {
    const nextEnabled = !user.enabled;
    setUserActionLoading((prev) => ({ ...prev, [user.username]: "toggle" }));

    try {
      const res = await fetch(`/api/center/users/${encodeURIComponent(user.username)}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders({ forAdmin: true }),
        },
        body: JSON.stringify({ enabled: nextEnabled }),
      });

      if (!res.ok) {
        const errData: any = await res.json().catch(() => ({}));
        throw new Error(errData?.error || `修改状态失败: HTTP ${res.status}`);
      }

      setUsers((prev) =>
        prev.map((u) => (u.username === user.username ? { ...u, enabled: nextEnabled } : u))
      );
      showToast(`账号 "${user.username}" 已${nextEnabled ? "启用" : "停用"}`, "success");
    } catch (err: any) {
      showToast(err?.message || "操作失败", "error");
    } finally {
      setUserActionLoading((prev) => {
        const next = { ...prev };
        delete next[user.username];
        return next;
      });
    }
  };

  /**
   * 打开新建用户弹窗
   */
  const handleOpenCreateModal = () => {
    setEditingUser(null);
    setFormUsername("");
    setFormPassword("");
    setFormRole("user");
    setFormPermission("rw");
    setFormRootPath("");
    setFormQuotaUnit("UNLIMITED");
    setFormQuotaValue(500);
    setFormShowPassword(false);
    setUserModalOpen(true);
  };

  /**
   * 打开编辑用户弹窗
   */
  const handleOpenEditModal = (user: UserItem) => {
    setEditingUser(user);
    setFormUsername(user.username);
    setFormPassword("");
    setFormRole(user.role);
    setFormPermission(user.permission);
    setFormRootPath(user.rootPath || "");
    setFormShowPassword(false);

    if (!user.quotaBytes || user.quotaBytes <= 0) {
      setFormQuotaUnit("UNLIMITED");
      setFormQuotaValue(500);
    } else if (user.quotaBytes >= 1024 * 1024 * 1024 && user.quotaBytes % (1024 * 1024 * 1024) === 0) {
      setFormQuotaUnit("GB");
      setFormQuotaValue(user.quotaBytes / (1024 * 1024 * 1024));
    } else {
      setFormQuotaUnit("MB");
      setFormQuotaValue(Math.round(user.quotaBytes / (1024 * 1024)));
    }

    setUserModalOpen(true);
  };

  /**
   * 提交新建或编辑用户
   */
  const handleSaveUser = async () => {
    if (!formUsername.trim()) {
      showToast("用户名不能为空", "error");
      return;
    }

    if (!editingUser && !formPassword) {
      showToast("新建用户必须设置初始密码", "error");
      return;
    }

    let calculatedQuotaBytes = 0;
    if (formQuotaUnit === "MB") {
      calculatedQuotaBytes = (formQuotaValue || 0) * 1024 * 1024;
    } else if (formQuotaUnit === "GB") {
      calculatedQuotaBytes = (formQuotaValue || 0) * 1024 * 1024 * 1024;
    }

    const payload: any = {
      role: formRole,
      permission: formPermission,
      rootPath: formRootPath,
      quotaBytes: calculatedQuotaBytes,
    };

    if (formPassword) {
      payload.password = formPassword;
    }

    setFormSubmitting(true);
    try {
      if (editingUser) {
        const res = await fetch(`/api/center/users/${encodeURIComponent(editingUser.username)}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            ...getAuthHeaders({ forAdmin: true }),
          },
          body: JSON.stringify(payload),
        });

        if (!res.ok) {
          const errData: any = await res.json().catch(() => ({}));
          throw new Error(errData?.error || `更新用户失败: HTTP ${res.status}`);
        }

        const data: any = await res.json();
        setUsers((prev) =>
          prev.map((u) => (u.username === editingUser.username ? { ...u, ...data.user } : u))
        );
        showToast(`用户 "${editingUser.username}" 信息更新成功`, "success");
      } else {
        payload.username = formUsername.trim();
        payload.enabled = true;

        const res = await fetch("/api/center/users", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...getAuthHeaders({ forAdmin: true }),
          },
          body: JSON.stringify(payload),
        });

        if (!res.ok) {
          const errData: any = await res.json().catch(() => ({}));
          throw new Error(errData?.error || `创建用户失败: HTTP ${res.status}`);
        }

        const data: any = await res.json();
        setUsers((prev) => [...prev, data.user]);
        showToast(`用户 "${payload.username}" 创建成功`, "success");
      }

      setUserModalOpen(false);
    } catch (err: any) {
      showToast(err?.message || "保存用户失败", "error");
    } finally {
      setFormSubmitting(false);
    }
  };

  /**
   * 打开快速重置密码模态框
   */
  const handleOpenPasswordModal = (username: string) => {
    setTargetPasswordUser(username);
    setNewPasswordValue("");
    setNewPasswordShow(false);
    setPasswordModalOpen(true);
  };

  /**
   * 提交重置密码
   */
  const handleSavePassword = async () => {
    if (!newPasswordValue) {
      showToast("新密码不能为空", "error");
      return;
    }

    setPasswordSubmitting(true);
    try {
      const res = await fetch(`/api/center/users/${encodeURIComponent(targetPasswordUser)}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders({ forAdmin: true }),
        },
        body: JSON.stringify({ password: newPasswordValue }),
      });

      if (!res.ok) {
        const errData: any = await res.json().catch(() => ({}));
        throw new Error(errData?.error || `修改密码失败: HTTP ${res.status}`);
      }

      showToast(`用户 "${targetPasswordUser}" 的密码已成功修改`, "success");
      setPasswordModalOpen(false);
    } catch (err: any) {
      showToast(err?.message || "修改密码失败", "error");
    } finally {
      setPasswordSubmitting(false);
    }
  };

  /**
   * 打开删除确认弹窗
   */
  const handleOpenDeleteModal = (user: UserItem) => {
    setTargetDeleteUser(user);
    setDeleteFilesCascade(false);
    setDeleteModalOpen(true);
  };

  /**
   * 确认执行删除用户
   */
  const handleConfirmDelete = async () => {
    if (!targetDeleteUser) return;
    setDeleteSubmitting(true);

    try {
      const query = deleteFilesCascade ? "?deleteFiles=true" : "?deleteFiles=false";
      const res = await fetch(`/api/center/users/${encodeURIComponent(targetDeleteUser.username)}${query}`, {
        method: "DELETE",
        headers: getAuthHeaders({ forAdmin: true }),
      });

      if (!res.ok) {
        const errData: any = await res.json().catch(() => ({}));
        throw new Error(errData?.error || `删除用户失败: HTTP ${res.status}`);
      }

      setUsers((prev) => prev.filter((u) => u.username !== targetDeleteUser.username));
      showToast(
        `用户 "${targetDeleteUser.username}" 已被删除${deleteFilesCascade ? "，物理文件已同步清理" : ""}`,
        "success"
      );
      setDeleteModalOpen(false);
    } catch (err: any) {
      showToast(err?.message || "删除用户失败", "error");
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const handleLogoutClick = () => {
    logout();
    onLogout();
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundImage: "url(/bg-dark.webp)",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: "fixed",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* 顶部导航栏 */}
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          backgroundColor: "rgba(18, 20, 32, 0.8)",
          borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
        }}
      >
        <Toolbar sx={{ justifyContent: "space-between", px: { xs: 2, md: 4 } }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                p: 1,
                borderRadius: "50%",
                backgroundColor: "rgba(107, 124, 255, 0.15)",
                color: "#8a99ff",
                display: "flex",
              }}
            >
              <AdminIcon sx={{ fontSize: 28 }} />
            </Box>
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="h6" fontWeight="bold" noWrap>
                  FlareDrive-Pro
                </Typography>
                <Chip
                  size="small"
                  label="控制中心"
                  color="primary"
                  sx={{
                    fontWeight: 600,
                    height: 22,
                    fontSize: "0.75rem",
                    backgroundColor: "rgba(107, 124, 255, 0.2)",
                    border: "1px solid rgba(107, 124, 255, 0.4)",
                  }}
                />
              </Box>
            </Box>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Button
              variant="outlined"
              color="inherit"
              startIcon={<DriveIcon />}
              onClick={onNavigateToDrive}
              sx={{
                borderColor: "rgba(255, 255, 255, 0.2)",
                borderRadius: 2,
                textTransform: "none",
                "&:hover": {
                  borderColor: "#6b7cff",
                  backgroundColor: "rgba(107, 124, 255, 0.1)",
                },
              }}
            >
              返回网盘
            </Button>

            <Tooltip title="刷新用户列表">
              <IconButton
                color="inherit"
                onClick={() => fetchUsers(true)}
                disabled={refreshing || loading}
                sx={{
                  border: "1px solid rgba(255, 255, 255, 0.12)",
                  borderRadius: 2,
                }}
              >
                {refreshing ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
              </IconButton>
            </Tooltip>

            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={handleOpenCreateModal}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                background: "linear-gradient(135deg, #6b7cff 0%, #8a5cf6 100%)",
                boxShadow: "0 4px 14px rgba(107, 124, 255, 0.4)",
              }}
            >
              新建用户
            </Button>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, borderColor: "rgba(255, 255, 255, 0.1)" }} />

            <Tooltip title={`当前账号: ${currentUser?.username || "admin"}`}>
              <Chip
                label={currentUser?.username || "admin"}
                avatar={<AdminIcon />}
                variant="outlined"
                sx={{
                  borderColor: "rgba(255, 255, 255, 0.2)",
                  color: "#fff",
                }}
              />
            </Tooltip>

            <Tooltip title="退出控制中心">
              <IconButton
                color="error"
                onClick={handleLogoutClick}
                sx={{
                  border: "1px solid rgba(244, 67, 54, 0.3)",
                  borderRadius: 2,
                }}
              >
                <LogoutIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>
      </AppBar>

      {/* 主体内容区 */}
      <Box sx={{ flexGrow: 1, p: { xs: 2, md: 4 }, maxWidth: 1600, width: "100%", mx: "auto" }}>
        {/* 概览指标卡片 */}
        <Grid container spacing={2.5} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                backdropFilter: "blur(16px)",
                backgroundColor: "rgba(23, 24, 36, 0.6)",
                border: "1px solid rgba(255, 255, 255, 0.08)",
                borderRadius: 3,
              }}
            >
              <CardContent sx={{ display: "flex", alignItems: "center", gap: 2, py: 2.5 }}>
                <Box sx={{ p: 1.5, borderRadius: 2.5, backgroundColor: "rgba(107, 124, 255, 0.15)", color: "#6b7cff" }}>
                  <PeopleIcon sx={{ fontSize: 32 }} />
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: "rgba(255, 255, 255, 0.6)" }}>
                    用户总数
                  </Typography>
                  <Typography variant="h5" fontWeight="bold">
                    {metrics.totalUsers}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                backdropFilter: "blur(16px)",
                backgroundColor: "rgba(23, 24, 36, 0.6)",
                border: "1px solid rgba(255, 255, 255, 0.08)",
                borderRadius: 3,
              }}
            >
              <CardContent sx={{ display: "flex", alignItems: "center", gap: 2, py: 2.5 }}>
                <Box sx={{ p: 1.5, borderRadius: 2.5, backgroundColor: "rgba(76, 175, 80, 0.15)", color: "#4caf50" }}>
                  <ActiveIcon sx={{ fontSize: 32 }} />
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: "rgba(255, 255, 255, 0.6)" }}>
                    正常启用账号
                  </Typography>
                  <Typography variant="h5" fontWeight="bold">
                    {metrics.activeUsers}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                backdropFilter: "blur(16px)",
                backgroundColor: "rgba(23, 24, 36, 0.6)",
                border: "1px solid rgba(255, 255, 255, 0.08)",
                borderRadius: 3,
              }}
            >
              <CardContent sx={{ display: "flex", alignItems: "center", gap: 2, py: 2.5 }}>
                <Box sx={{ p: 1.5, borderRadius: 2.5, backgroundColor: "rgba(255, 152, 0, 0.15)", color: "#ff9800" }}>
                  <StorageIcon sx={{ fontSize: 32 }} />
                </Box>
                <Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Typography variant="caption" sx={{ color: "rgba(255, 255, 255, 0.6)" }}>
                      总已用物理存储
                    </Typography>
                    <Tooltip title="真实物理存储占用（智能识别全桶与子目录前缀包含关系，避免嵌套目录重复计算）">
                      <Box component="span" sx={{ fontSize: "0.75rem", color: "rgba(255, 255, 255, 0.4)", cursor: "help" }}>
                        ⓘ
                      </Box>
                    </Tooltip>
                  </Box>
                  <Typography variant="h5" fontWeight="bold">
                    {humanReadableSize(metrics.totalUsedBytes)}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                backdropFilter: "blur(16px)",
                backgroundColor: "rgba(23, 24, 36, 0.6)",
                border: "1px solid rgba(255, 255, 255, 0.08)",
                borderRadius: 3,
              }}
            >
              <CardContent sx={{ display: "flex", alignItems: "center", gap: 2, py: 2.5 }}>
                <Box sx={{ p: 1.5, borderRadius: 2.5, backgroundColor: "rgba(244, 67, 54, 0.15)", color: "#f44336" }}>
                  <AdminIcon sx={{ fontSize: 32 }} />
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: "rgba(255, 255, 255, 0.6)" }}>
                    管理权限账号
                  </Typography>
                  <Typography variant="h5" fontWeight="bold">
                    {metrics.adminCount}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* 用户管理表格卡片 */}
        <Card
          sx={{
            backdropFilter: "blur(20px)",
            backgroundColor: "rgba(20, 22, 34, 0.8)",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            borderRadius: 3,
            boxShadow: "0 10px 40px rgba(0, 0, 0, 0.4)",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              p: 2.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
              flexWrap: "wrap",
              gap: 1.5,
            }}
          >
            <Box>
              <Typography variant="h6" fontWeight="bold">
                用户账号与存储配额列表 ({users.length})
              </Typography>
              <Typography variant="caption" sx={{ color: "rgba(255, 255, 255, 0.5)" }}>
                零外部数据库依赖 • AES-256-GCM 密文存储 • RFC 4331 存储配额
              </Typography>
            </Box>

            <Button
              size="small"
              variant="outlined"
              color="primary"
              startIcon={recalcingAll ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
              disabled={recalcingAll || loading || users.length === 0}
              onClick={handleRecalcAll}
              sx={{
                borderColor: "rgba(107, 124, 255, 0.4)",
                borderRadius: 2,
                textTransform: "none",
                color: "#8a99ff",
                "&:hover": {
                  borderColor: "#6b7cff",
                  backgroundColor: "rgba(107, 124, 255, 0.1)",
                },
              }}
            >
              {recalcingAll ? "正在校准全部..." : "一键校准全部空间"}
            </Button>
          </Box>

          <TableContainer component={Box}>
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", p: 8 }}>
                <CircularProgress size={40} />
              </Box>
            ) : users.length === 0 ? (
              <Box sx={{ textAlign: "center", p: 8, color: "rgba(255, 255, 255, 0.5)" }}>
                暂无用户数据
              </Box>
            ) : (
              <Table sx={{ minWidth: 850 }}>
                <TableHead sx={{ backgroundColor: "rgba(255, 255, 255, 0.03)" }}>
                  <TableRow>
                    <TableCell sx={{ color: "rgba(255, 255, 255, 0.7)", fontWeight: 600 }}>用户名</TableCell>
                    <TableCell sx={{ color: "rgba(255, 255, 255, 0.7)", fontWeight: 600 }}>角色</TableCell>
                    <TableCell sx={{ color: "rgba(255, 255, 255, 0.7)", fontWeight: 600 }}>根目录 (rootPath)</TableCell>
                    <TableCell sx={{ color: "rgba(255, 255, 255, 0.7)", fontWeight: 600 }}>权限</TableCell>
                    <TableCell sx={{ color: "rgba(255, 255, 255, 0.7)", fontWeight: 600, minWidth: 200 }}>
                      存储空间 (已用 / 配额)
                    </TableCell>
                    <TableCell sx={{ color: "rgba(255, 255, 255, 0.7)", fontWeight: 600 }}>状态</TableCell>
                    <TableCell align="right" sx={{ color: "rgba(255, 255, 255, 0.7)", fontWeight: 600, pr: 3 }}>
                      操作
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user) => {
                    const isUnlimited = !user.quotaBytes || user.quotaBytes <= 0;
                    const used = Number(user.usedBytes) || 0;
                    const quota = Number(user.quotaBytes) || 0;
                    const percent = isUnlimited ? 0 : Math.min(100, Math.round((used / quota) * 100));

                    let barColor = "#6b7cff";
                    if (percent > 90) barColor = "#f44336";
                    else if (percent > 75) barColor = "#ff9800";

                    const isRecalcing = userActionLoading[user.username] === "recalc";
                    const isToggling = userActionLoading[user.username] === "toggle";

                    return (
                      <TableRow
                        key={user.username}
                        hover
                        sx={{
                          "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.03)" },
                          borderBottom: "1px solid rgba(255, 255, 255, 0.05)",
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            {user.username}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Chip
                            size="small"
                            label={user.role === "admin" ? "超级管理员" : "普通用户"}
                            color={user.role === "admin" ? "secondary" : "default"}
                            sx={{
                              height: 24,
                              fontSize: "0.75rem",
                              backgroundColor:
                                user.role === "admin"
                                  ? "rgba(138, 92, 246, 0.2)"
                                  : "rgba(255, 255, 255, 0.08)",
                              color: user.role === "admin" ? "#b39ddb" : "rgba(255, 255, 255, 0.8)",
                              border: "1px solid rgba(255, 255, 255, 0.08)",
                            }}
                          />
                        </TableCell>

                        <TableCell>
                          <Tooltip title={user.rootPath === "" ? "全桶根目录（所有文件可见）" : `隔离子目录: ${user.rootPath}`}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                              <FolderIcon sx={{ fontSize: 16, color: "rgba(255, 255, 255, 0.5)" }} />
                              <Typography variant="body2" sx={{ fontFamily: "monospace", color: "rgba(255, 255, 255, 0.85)" }}>
                                {user.rootPath === "" ? "[全桶根目录]" : user.rootPath}
                              </Typography>
                            </Box>
                          </Tooltip>
                        </TableCell>

                        <TableCell>
                          <Chip
                            size="small"
                            label={user.permission === "rw" ? "读写 (RW)" : "只读 (RO)"}
                            color={user.permission === "rw" ? "success" : "warning"}
                            variant="outlined"
                            sx={{ height: 22, fontSize: "0.72rem" }}
                          />
                        </TableCell>

                        <TableCell>
                          <Box sx={{ width: "100%", pr: 2 }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                              <Typography variant="caption" sx={{ color: "rgba(255, 255, 255, 0.8)" }}>
                                {humanReadableSize(used)}
                              </Typography>
                              <Typography variant="caption" sx={{ color: "rgba(255, 255, 255, 0.5)" }}>
                                {isUnlimited ? "无限制" : `${humanReadableSize(quota)} (${percent}%)`}
                              </Typography>
                            </Box>
                            {!isUnlimited && (
                              <LinearProgress
                                variant="determinate"
                                value={percent}
                                sx={{
                                  height: 6,
                                  borderRadius: 3,
                                  backgroundColor: "rgba(255, 255, 255, 0.08)",
                                  "& .MuiLinearProgress-bar": {
                                    backgroundColor: barColor,
                                    borderRadius: 3,
                                  },
                                }}
                              />
                            )}
                          </Box>
                        </TableCell>

                        <TableCell>
                          <Switch
                            checked={user.enabled}
                            disabled={isToggling}
                            onChange={() => handleToggleEnabled(user)}
                            color="primary"
                            size="small"
                          />
                        </TableCell>

                        <TableCell align="right" sx={{ pr: 2 }}>
                          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.5 }}>
                            <Tooltip title="校准空间 (遍历真实物理对象)">
                              <IconButton
                                size="small"
                                disabled={isRecalcing}
                                onClick={() => handleRecalcQuota(user.username)}
                                sx={{ color: "#8a99ff" }}
                              >
                                {isRecalcing ? <CircularProgress size={16} /> : <SyncIcon fontSize="small" />}
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="编辑用户配置">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenEditModal(user)}
                                sx={{ color: "rgba(255, 255, 255, 0.7)" }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="重置密码">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenPasswordModal(user.username)}
                                sx={{ color: "rgba(255, 255, 255, 0.7)" }}
                              >
                                <KeyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="删除用户">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleOpenDeleteModal(user)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </TableContainer>
        </Card>
      </Box>

      {/* 新建/编辑用户模态框 */}
      <Dialog
        open={userModalOpen}
        onClose={() => !formSubmitting && setUserModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backdropFilter: "blur(24px)",
            backgroundColor: "rgba(23, 25, 38, 0.95)",
            border: "1px solid rgba(255, 255, 255, 0.12)",
            borderRadius: 3,
            p: 1,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: "bold" }}>
          {editingUser ? `编辑用户: ${editingUser.username}` : "新建用户"}
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: "rgba(255, 255, 255, 0.08)" }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: 1 }}>
            {!editingUser && (
              <TextField
                label="登录用户名"
                fullWidth
                size="small"
                value={formUsername}
                onChange={(e) => setFormUsername(e.target.value)}
                placeholder="例如: alice, bob"
              />
            )}

            <TextField
              label={editingUser ? "修改密码 (留空则保持不变)" : "初始密码"}
              type={formShowPassword ? "text" : "password"}
              fullWidth
              size="small"
              value={formPassword}
              onChange={(e) => setFormPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setFormShowPassword(!formShowPassword)}>
                      {formShowPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <FormControl component="fieldset">
              <FormLabel component="legend" sx={{ fontSize: "0.85rem", color: "rgba(255, 255, 255, 0.7)" }}>
                账号角色
              </FormLabel>
              <RadioGroup
                row
                value={formRole}
                onChange={(e) => setFormRole(e.target.value as "admin" | "user")}
              >
                <FormControlLabel value="user" control={<Radio size="small" />} label="普通用户" />
                <FormControlLabel value="admin" control={<Radio size="small" />} label="超级管理员" />
              </RadioGroup>
            </FormControl>

            <FormControl component="fieldset">
              <FormLabel component="legend" sx={{ fontSize: "0.85rem", color: "rgba(255, 255, 255, 0.7)" }}>
                权限模式
              </FormLabel>
              <RadioGroup
                row
                value={formPermission}
                onChange={(e) => setFormPermission(e.target.value as "rw" | "ro")}
              >
                <FormControlLabel value="rw" control={<Radio size="small" />} label="读写权限 (RW)" />
                <FormControlLabel value="ro" control={<Radio size="small" />} label="只读模式 (RO)" />
              </RadioGroup>
            </FormControl>

            <Box>
              <FormLabel sx={{ fontSize: "0.85rem", color: "rgba(255, 255, 255, 0.7)", mb: 0.5, display: "block" }}>
                存储根目录前缀 (rootPath)
              </FormLabel>
              <TextField
                fullWidth
                size="small"
                value={formRootPath}
                onChange={(e) => setFormRootPath(e.target.value)}
                placeholder='空代表全桶，或如 "users/alice/", "shared/docs/"'
              />
              <Box sx={{ mt: 1, display: "flex", gap: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setFormRootPath("")}
                  sx={{ textTransform: "none", fontSize: "0.75rem" }}
                >
                  全桶根目录 ("")
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setFormRootPath(`users/${formUsername || "user"}/`)}
                  sx={{ textTransform: "none", fontSize: "0.75rem" }}
                >
                  用户专属目录 ("users/...")
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setFormRootPath("shared/public/")}
                  sx={{ textTransform: "none", fontSize: "0.75rem" }}
                >
                  公共共享 ("shared/public/")
                </Button>
              </Box>
            </Box>

            <Box>
              <FormLabel sx={{ fontSize: "0.85rem", color: "rgba(255, 255, 255, 0.7)", mb: 0.5, display: "block" }}>
                存储配额限制 (Quota)
              </FormLabel>
              <Box sx={{ display: "flex", gap: 1.5 }}>
                <Select
                  size="small"
                  value={formQuotaUnit}
                  onChange={(e) => setFormQuotaUnit(e.target.value as any)}
                  sx={{ width: 140 }}
                >
                  <MenuItem value="UNLIMITED">无限制 (0)</MenuItem>
                  <MenuItem value="MB">按 MB 设定</MenuItem>
                  <MenuItem value="GB">按 GB 设定</MenuItem>
                </Select>

                {formQuotaUnit !== "UNLIMITED" && (
                  <TextField
                    type="number"
                    size="small"
                    value={formQuotaValue}
                    onChange={(e) => setFormQuotaValue(Math.max(1, Number(e.target.value)))}
                    sx={{ flexGrow: 1 }}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">{formQuotaUnit}</InputAdornment>,
                    }}
                  />
                )}
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setUserModalOpen(false)} disabled={formSubmitting} color="inherit">
            取消
          </Button>
          <Button
            onClick={handleSaveUser}
            disabled={formSubmitting}
            variant="contained"
            sx={{
              background: "linear-gradient(135deg, #6b7cff 0%, #8a5cf6 100%)",
            }}
          >
            {formSubmitting ? <CircularProgress size={20} /> : "保 存"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 修改密码模态框 */}
      <Dialog
        open={passwordModalOpen}
        onClose={() => !passwordSubmitting && setPasswordModalOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            backdropFilter: "blur(24px)",
            backgroundColor: "rgba(23, 25, 38, 0.95)",
            border: "1px solid rgba(255, 255, 255, 0.12)",
            borderRadius: 3,
            p: 1,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: "bold" }}>修改用户密码</DialogTitle>
        <DialogContent dividers sx={{ borderColor: "rgba(255, 255, 255, 0.08)", pt: 2 }}>
          <Typography variant="body2" sx={{ mb: 2, color: "rgba(255, 255, 255, 0.7)" }}>
            正在为用户 <strong>{targetPasswordUser}</strong> 重置登录密码：
          </Typography>
          <TextField
            label="新密码"
            type={newPasswordShow ? "text" : "password"}
            fullWidth
            size="small"
            value={newPasswordValue}
            onChange={(e) => setNewPasswordValue(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setNewPasswordShow(!newPasswordShow)}>
                    {newPasswordShow ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setPasswordModalOpen(false)} disabled={passwordSubmitting} color="inherit">
            取消
          </Button>
          <Button
            onClick={handleSavePassword}
            disabled={passwordSubmitting}
            variant="contained"
            color="primary"
          >
            {passwordSubmitting ? <CircularProgress size={20} /> : "确 认"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 删除确认模态框 */}
      <Dialog
        open={deleteModalOpen}
        onClose={() => !deleteSubmitting && setDeleteModalOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            backdropFilter: "blur(24px)",
            backgroundColor: "rgba(23, 25, 38, 0.95)",
            border: "1px solid rgba(255, 255, 255, 0.12)",
            borderRadius: 3,
            p: 1,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: "bold", color: "#f44336" }}>确认删除用户？</DialogTitle>
        <DialogContent dividers sx={{ borderColor: "rgba(255, 255, 255, 0.08)", pt: 2 }}>
          <Typography variant="body2" sx={{ color: "rgba(255, 255, 255, 0.8)", mb: 2 }}>
            您确定要彻底删除账号 <strong>{targetDeleteUser?.username}</strong> 吗？此操作不可恢复。
          </Typography>
          {targetDeleteUser?.rootPath && targetDeleteUser.rootPath !== "" && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={deleteFilesCascade}
                  onChange={(e) => setDeleteFilesCascade(e.target.checked)}
                  color="error"
                />
              }
              label={
                <Typography variant="body2" color="error">
                  同时彻底清理存储桶物理文件 ({targetDeleteUser.rootPath})
                </Typography>
              }
            />
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteModalOpen(false)} disabled={deleteSubmitting} color="inherit">
            取消
          </Button>
          <Button
            onClick={handleConfirmDelete}
            disabled={deleteSubmitting}
            variant="contained"
            color="error"
          >
            {deleteSubmitting ? <CircularProgress size={20} /> : "确认删除"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 提示消息 */}
      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
          severity={toast.severity}
          sx={{ width: "100%", borderRadius: 2 }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminDashboardPage;
